import { StoredTextureLike } from '@utils/sceneObjects/linked/textureCatalogApi'
import { isUserAppliedLinkedTexture } from '@utils/sceneObjects/linked/reconcileTexturesAfterUnitChange'

type ZoneEntry = {
  name?: string
  material?: {
    zoneDisplayName?: string
    zone?: string
    linkedTextureID?: number
  }
  idAcabadoUnidadModeloAcabado?: string
}

const KNOWN_DISPLAY_ZONE_NAMES = new Set([
  'Puerta',
  'Zocalo',
  'Zócalo',
  'Armazon',
  'Armazón',
  'Patas',
  'Gola',
  'Costado',
  'Encimera',
  'Frente',
])

/** Nombre lógico de zona (Puerta, Zócalo…) frente a clave de mesh (Mesh001_2). */
export function isMeshZoneKey(name: string): boolean {
  return /^Mesh\d+/i.test(name.trim())
}

function countZonesWithDisplayName(
  zones: Record<string, ZoneEntry>,
  displayName: string,
): number {
  let count = 0
  for (const [key, zone] of Object.entries(zones)) {
    const mat = zone?.material
    if (!mat) continue
    if (
      mat.zoneDisplayName === displayName ||
      mat.zone === displayName ||
      key === displayName ||
      zone.name === displayName
    ) {
      count++
    }
  }
  return count
}

function findUniqueZoneKeyByDisplayName(
  zones: Record<string, ZoneEntry>,
  displayName: string,
): string | null {
  if (countZonesWithDisplayName(zones, displayName) !== 1) return null

  for (const [key, zone] of Object.entries(zones)) {
    const mat = zone?.material
    if (!mat) continue
    if (
      mat.zoneDisplayName === displayName ||
      mat.zone === displayName ||
      key === displayName ||
      zone.name === displayName
    ) {
      return key
    }
  }
  return null
}

function findZoneKeyByExactAcabadoId(
  zones: Record<string, ZoneEntry>,
  compositeId: string | number,
): string | null {
  const id = String(compositeId)
  for (const [key, zone] of Object.entries(zones)) {
    const zoneComposite = zone?.idAcabadoUnidadModeloAcabado
    if (zoneComposite && String(zoneComposite) === id) return key
  }
  return null
}

/**
 * Resuelve la clave de `zones` para una entrada de `textures[]`.
 * Prioridad: zoneName mesh exacto → zone.name → displayName único → idAcabado exacto.
 */
export function findZoneKeyForTexture(
  zones: Record<string, ZoneEntry>,
  texture: StoredTextureLike,
): string | null {
  if (!zones || !texture) return null

  const zoneName = texture.zoneName?.trim()
  if (zoneName) {
    if (zones[zoneName]) return zoneName

    for (const [key, zone] of Object.entries(zones)) {
      if (zone?.name === zoneName || key === zoneName) {
        return key
      }
    }

    if (isMeshZoneKey(zoneName) || KNOWN_DISPLAY_ZONE_NAMES.has(zoneName)) {
      const byLogicalName = findUniqueZoneKeyByDisplayName(zones, zoneName)
      if (byLogicalName) return byLogicalName
    } else if (!isMeshZoneKey(zoneName)) {
      const byLogicalName = findUniqueZoneKeyByDisplayName(zones, zoneName)
      if (byLogicalName) return byLogicalName
    }
  }

  const displayName = texture.zoneDisplayName || texture.zone
  if (displayName) {
    const byDisplay = findUniqueZoneKeyByDisplayName(zones, displayName)
    if (byDisplay) return byDisplay
  }

  const compositeId = texture.idAcabadoUnidadModeloAcabado
  if (compositeId) {
    return findZoneKeyByExactAcabadoId(zones, compositeId)
  }

  return null
}

/** Ordena texturas: claves mesh exactas primero, luego nombres más específicos. */
export function sortTexturesForZoneSync<T extends StoredTextureLike>(
  textures: T[],
  zones?: Record<string, ZoneEntry>,
): T[] {
  return [...textures].sort((a, b) => {
    const aExact = zones && a.zoneName && zones[a.zoneName] ? 1 : 0
    const bExact = zones && b.zoneName && zones[b.zoneName] ? 1 : 0
    if (aExact !== bExact) return bExact - aExact

    const aMesh = a.zoneName && isMeshZoneKey(a.zoneName) ? 1 : 0
    const bMesh = b.zoneName && isMeshZoneKey(b.zoneName) ? 1 : 0
    if (aMesh !== bMesh) return bMesh - aMesh

    const lenA = a.zoneName?.length ?? 0
    const lenB = b.zoneName?.length ?? 0
    return lenB - lenA
  })
}

/**
 * Tras cambio de GLB, alinea zoneName/linkedTextureID de textures con las zonas reales del mesh.
 */
export function alignTextureZoneNamesWithZones(
  textures: StoredTextureLike[],
  zones: Record<string, ZoneEntry>,
): StoredTextureLike[] {
  return textures.map((texture) => {
    const zoneKey = findZoneKeyForTexture(zones, texture)
    if (!zoneKey) return texture

    const zone = zones[zoneKey]
    const mat = zone?.material
    const linkedFromZone = mat?.linkedTextureID

    return {
      ...texture,
      zoneName: zoneKey,
      zoneDisplayName: mat?.zoneDisplayName ?? texture.zoneDisplayName,
      zone: mat?.zone ?? mat?.zoneDisplayName ?? texture.zone,
      linkedTextureID:
        typeof linkedFromZone === 'number' && linkedFromZone > 0
          ? linkedFromZone
          : texture.linkedTextureID,
    }
  })
}

/** Resuelve la clave mesh en `zones` para aplicar o sincronizar una textura. */
export function resolveZoneKeyForTarget(
  zones: Record<string, ZoneEntry>,
  targetTexture: Pick<StoredTextureLike, 'zoneName' | 'zoneDisplayName' | 'zone'>,
): string | null {
  if (!zones || !targetTexture) return null

  const zoneName = targetTexture.zoneName?.trim()
  if (zoneName && zones[zoneName]) return zoneName

  return findZoneKeyForTexture(zones, targetTexture as StoredTextureLike)
}

export function findTextureForMeshNode(
  nodeName: string,
  zones: Record<string, ZoneEntry>,
  textures: StoredTextureLike[],
): StoredTextureLike | undefined {
  const direct = textures.find((t) => t.zoneName === nodeName)
  if (direct) return direct

  const zoneMaterial = zones[nodeName]?.material
  const displayName = zoneMaterial?.zoneDisplayName || zoneMaterial?.zone
  if (!displayName) return undefined

  const matches = textures.filter(
    (t) => t.zoneDisplayName === displayName || t.zone === displayName,
  )
  if (matches.length !== 1) return undefined

  const resolvedKey = findZoneKeyForTexture(zones, matches[0])
  return resolvedKey === nodeName ? matches[0] : undefined
}

export function textureZoneAlreadyProcessed(
  processedZoneNames: Set<string>,
  texture: Pick<StoredTextureLike, 'zoneName' | 'zoneDisplayName'>,
  newTextures: Array<Pick<StoredTextureLike, 'zoneName'>>,
): boolean {
  if (texture.zoneName && processedZoneNames.has(texture.zoneName)) {
    return true
  }

  return newTextures.some(
    (entry) =>
      Boolean(texture.zoneName) &&
      Boolean(entry.zoneName) &&
      entry.zoneName === texture.zoneName,
  )
}

/**
 * Empareja la textura aplicada por el usuario con una zona del NUEVO GLB usando la
 * zona lógica (`zoneDisplayName`/`zone`) como clave estable.
 *
 * Necesario al cambiar de unidad (p. ej. Gola → Lineal): las claves de mesh
 * (`Mesh001_1`…) se desplazan cuando aparecen/desaparecen zonas (Gola), de modo que
 * emparejar por clave de mesh asigna la textura a la zona equivocada (el Armazón
 * heredaría la visual de la Gola). El nombre lógico de zona no se desplaza.
 */
export function resolveUserTextureByDisplayName<T extends StoredTextureLike>(
  zoneDisplayName: string,
  zone: string,
  textures: T[],
): T | undefined {
  const targets = [zoneDisplayName, zone].filter(Boolean)
  if (targets.length === 0) return undefined

  for (const target of targets) {
    const matches = textures.filter(
      (t) => t.zoneDisplayName === target || t.zone === target,
    )
    if (matches.length === 1) return matches[0]
    if (matches.length > 1) {
      // Varias coincidencias (raro): preferir la que el usuario aplicó realmente.
      const userApplied = matches.find((t) => isUserAppliedLinkedTexture(t))
      if (userApplied) return userApplied
      return matches[0]
    }
  }

  return undefined
}

/** Busca textura de usuario para un mesh del GLB; prioriza zoneName sobre displayName. */
export function resolveUserTextureForGlbZone<T extends StoredTextureLike>(
  zoneName: string,
  zoneDisplayName: string,
  textures: T[],
  primaryByZoneName: Map<string, T>,
  fallbackByZoneName: Map<string, T>,
): T | undefined {
  const byZone =
    primaryByZoneName.get(zoneName) ??
    fallbackByZoneName.get(zoneName) ??
    textures.find((t) => t.zoneName === zoneName)

  if (byZone) return byZone

  const displayMatches = textures.filter(
    (t) => t.zoneDisplayName === zoneDisplayName || t.zone === zoneDisplayName,
  )
  if (displayMatches.length === 1) {
    return displayMatches[0]
  }

  return undefined
}
